import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, Truck, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { apiRequest } from '../lib/utils';
import DeliveryZoneMap from '../components/DeliveryZoneMap';

// Replace with official Google Business review short link (g.page/r/...) when available
const GOOGLE_REVIEW_URL = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('Tasty Bites, Via Luigi Lavizzari 5, 6900 Lugano');

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await apiRequest('/contact', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: t('contactInfo.phone'),
      details: t('contactInfo.phoneNumber'),
      subtext: t('contactInfo.phoneHours'),
      href: `tel:${t('contactInfo.phoneNumber').replace(/\s/g, '')}`
    },
    {
      icon: Mail,
      title: t('contactInfo.email'),
      details: t('contactInfo.emailAddress'),
      subtext: t('contactInfo.emailResponse'),
      href: `mailto:${t('contactInfo.emailAddress')}`
    },
    {
      icon: MapPin,
      title: t('contactInfo.address'),
      details: t('contactInfo.street'),
      subtext: t('contactInfo.city'),
      href: `https://maps.google.com/?q=${encodeURIComponent(t('contactInfo.street') + ', ' + t('contactInfo.city'))}`
    },
    {
      icon: Clock,
      title: t('contactInfo.hours'),
      details: t('contactInfo.hoursTime'),
      subtext: t('contactInfo.hoursOpen'),
      href: null
    }
  ];

  return (
    <div className="min-h-screen py-12 md:py-20">
      <Helmet>
        <title>Contact | Tasty Bites - Halal Food Takeaway Lugano</title>
        <meta name="description" content="Contact Tasty Bites in Lugano. Via Luigi Lavizzari 5, 6900 Lugano. Phone: 091 923 14 28. Delivery & pickup available." />
        <link rel="canonical" href="https://www.tastybiteslugano.ch/contact" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
            {t('contact.title')} <span className="text-[#FFC107]">{t('contact.us')}</span>
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-[#141417]/60 border border-[#27272A] rounded-xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-white mb-6 font-['Barlow_Condensed'] uppercase">
              {t('contact.sendMessage')} <span className="text-[#FFC107]">{t('contact.message')}</span>
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-300">
                    {t('contact.name')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.name')}
                    className="bg-[#1C1C22] border-zinc-700 focus:border-[#FFC107]"
                    data-testid="contact-name-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-300">
                    {t('contact.email')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.email')}
                    className="bg-[#1C1C22] border-zinc-700 focus:border-[#FFC107]"
                    data-testid="contact-email-input"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-zinc-300">{t('contact.phone')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('contact.phonePlaceholder')}
                    className="bg-[#1C1C22] border-zinc-700 focus:border-[#FFC107]"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-zinc-300">{t('contact.subject')}</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t('contact.subjectPlaceholder')}
                    className="bg-[#1C1C22] border-zinc-700 focus:border-[#FFC107]"
                    data-testid="contact-subject-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-zinc-300">
                  {t('contact.yourMessage')} <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.messagePlaceholder')}
                  rows={5}
                  className="bg-[#1C1C22] border-zinc-700 focus:border-[#FFC107] resize-none"
                  data-testid="contact-message-input"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wider"
                data-testid="contact-submit-button"
              >
                {loading ? (
                  t('contact.sending')
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    {t('contact.send')}
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {contactInfo.map((info, index) => {
                const Wrapper = info.href ? 'a' : 'div';
                const linkProps = info.href ? { href: info.href, target: info.href.startsWith('http') ? '_blank' : undefined, rel: info.href.startsWith('http') ? 'noopener noreferrer' : undefined } : {};
                return (
                  <Wrapper
                    key={index}
                    {...linkProps}
                    className={`bg-[#141417]/60 border border-[#27272A] rounded-xl p-5 transition-colors block ${info.href ? 'hover:border-[#FFC107]/50 cursor-pointer active:scale-[0.98]' : 'hover:border-[#FFC107]/30'}`}
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-[#FFC107]/10">
                        <info.icon className="h-5 w-5 text-[#FFC107]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-1">{info.title}</h3>
                        <p className={`text-sm ${info.href ? 'text-[#FFC107]' : 'text-zinc-300'}`}>{info.details}</p>
                        <p className="text-zinc-500 text-xs mt-1">{info.subtext}</p>
                      </div>
                    </div>
                  </Wrapper>
                );
              })}
            </div>

            {/* Map placeholder or additional info */}
            <div className="bg-[#141417]/60 border border-[#27272A] rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 font-['Barlow_Condensed'] uppercase">
                {t('contact.whyChoose')} <span className="text-[#FFC107]">Tasty Bites</span>?
              </h3>
              <ul className="space-y-3 text-zinc-300">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FFC107] rounded-full"></span>
                  {t('contact.halalFood')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FFC107] rounded-full"></span>
                  {t('contact.freshDaily')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FFC107] rounded-full"></span>
                  {t('contact.fastDeliveryTime')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FFC107] rounded-full"></span>
                  {t('contact.wideVariety')}
                </li>
              </ul>
              
              {/* Notices (TEMPORARY: delivery zone notice hidden while delivery is suspended) */}
              {false && (
              <div className="mt-6 space-y-3">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Truck className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <p className="text-green-500 font-medium text-sm">
                      {t('features.freeDeliveryNotice')}
                    </p>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Location Map */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-wider text-center">
            {t('contact.findUs', 'Find Us')}
          </h2>
          <DeliveryZoneMap height="450px" />
          <div className="mt-6 flex flex-col items-center gap-2">
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-[#FFC107] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#F59E0B] transition-colors active:scale-[0.98]"
              data-testid="rate-us-google-button"
            >
              <Star className="h-4 w-4 fill-black" />
              {t('contact.rateUs', 'Rate us on Google')}
            </a>
            <p className="text-zinc-500 text-xs">{t('contact.rateUsSubtitle', 'Enjoyed your meal? A quick review helps us a lot!')}</p>
          </div>
        </div>

        {/* Internal navigation links */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4 text-sm">
          <Link to="/" className="text-zinc-500 hover:text-[#FFC107] transition-colors">{t('nav.home')}</Link>
          <span className="text-zinc-700">|</span>
          <Link to="/menu" className="text-zinc-500 hover:text-[#FFC107] transition-colors">{t('nav.menu')}</Link>
          <span className="text-zinc-700">|</span>
          <Link to="/privacy" className="text-zinc-500 hover:text-[#FFC107] transition-colors">{t('privacy.title')}</Link>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
